import React, { useEffect, useState, useRef } from 'react';
import {
    IconButton,
    OutlinedInput,
    Select,
    MenuItem,
    Box, 
    Tabs,
    Tab,
} from '@mui/material';
import { AccessTime, Place, Notes } from '@mui/icons-material'; 
import ColorDropdown from './colorDropDown';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import MeetingLocation from './meetingLocation';
import { useBookingTypeContext } from './bookingTypeProvider'; 
import { MeetingOptions, MeetingTypes } from '../../../../../platform/util/meetingTypes';

// Booking type options
const bookingTypeOptions = [
    { id: 1, name: 'Event', applied: 'A diverse range of gatherings...' },
    { id: 2, name: 'Service', applied: 'An offering that provides assistance...' },
    { id: 3, name: 'Class', applied: 'Structured educational sessions...' },
];

const BookingBasicPart1 = (props) => {
    const { 
        isDisabled,
        handleChange, 
        name, 
        bookingTypeLength,
        setBookingTypeLength,
        optionsSelect,
        hrsOmins,
        setHrsOMins,
        typeOTime,
        setType, 
        meetingLocationTypeKey,
        setMeetingLocationTypeKey, 
        setLocationType, 
        locationInput, 
        desc,
        setDesc,
        setName,
        setBookingTypeCategoryId,
        bookingTypeCategoryId,  
        setLocationInput,
        phoneNumber,
        setPhoneNumber,
        canInviteeCall,
        setCanInviteeCall,
        redirectToVideo,
        handleInputChange,
        colorsMap,
        color,
        setColor,
        colors,
    } = props
    const {
        handlePopper,
        setHoveredColor,
        invertColor,
        errors,
       
    } = useBookingTypeContext();

    const [editableDesc, setEditableDesc] = useState(false);
    const descRef = useRef(null);
    const [tabValue, setTabValue] = useState(
        (bookingTypeCategoryId != null ? bookingTypeCategoryId - 1 : 0)
    ); 

    const handleRadioChange = (event) => {
        const value = event.target.value === 'true';  // Converts string to boolean
        setCanInviteeCall(value);  // Update the state with the boolean value
    };
    // Warn user if they try to leave without selecting a booking type
    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (!bookingTypeCategoryId) {
                const message = 'Please select an option before leaving the page.';
                event.returnValue = message;
                return message;
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [bookingTypeCategoryId]);

    // Close description editor on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (descRef.current && !descRef.current.contains(event.target)) {
                setEditableDesc(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Open description editor
    const handleDescriptionClick = () => {
        setEditableDesc(true); // Set editable state to true
    }; 
    
    return (
        <Box sx={{ mt: "30px", minHeight: "390px", color: 'text.primary',
            '& .input-label': { color: 'text.primary' },
            '& .ql-toolbar, & .ql-container': { borderColor: 'divider', bgcolor: 'background.default', color: 'text.primary' },
            '& .ql-toolbar': { borderRadius: '6px 6px 0 0' },
            '& .ql-container': { borderRadius: '0 0 6px 6px' },
            '& .ql-editor.ql-blank::before': { color: 'text.secondary' },
            '& .ql-snow .ql-stroke': { stroke: 'currentColor' },
            '& .ql-snow .ql-fill': { fill: 'currentColor' },
            '& .ql-snow .ql-picker, & .ql-toolbar button': { color: 'text.secondary' },
            '& .ql-snow .ql-picker-options, & .ql-snow .ql-tooltip': { bgcolor: 'background.paper', color: 'text.primary', borderColor: 'divider', boxShadow: 3 },
            '& .ql-snow .ql-tooltip input': { bgcolor: 'background.default', color: 'text.primary', borderColor: 'divider' },
            '& .ql-snow a': { color: 'primary.main' },
            '& .ql-toolbar button:hover, & .ql-toolbar button.ql-active, & .ql-snow .ql-picker-item:hover, & .ql-snow .ql-picker-item.ql-selected': { color: 'primary.main' },
        }}>
            <Tabs
                value={tabValue}
                onChange={(event, newValue) => {
                    setTabValue(newValue);
                    setBookingTypeCategoryId(bookingTypeOptions[newValue].id);
                }}
                indicatorColor="none"
                sx={{
                    marginBottom: 2,
                    "& .MuiTab-root": {
                        marginLeft: "4px",  // Compact spacing between tabs
                        minWidth: "10px",  // Adjusted minimum width
                        minHeight: "10px",  // Maintain minimum height
                        textTransform: "none",  // No uppercase transform
                        fontSize: "0.75rem",  // Smaller font size for a compact look
                        lineHeight: "1.2",  // Compact line height 
                        color: "text.secondary",
                        borderRadius: "8px",  // Rounded corners
                        transition: "color 0.3s, background-color 0.3s", // Smooth transition for hover
                        backgroundColor: "transparent", // Set default background to transparent
                    },
                    "& .Mui-selected": {
                        backgroundColor: "action.selected",
                        color: "primary.main",
                        border: "none",  // Remove border when selected
                    },
                    "& .MuiTab-root:hover": {
                        backgroundColor: "action.hover",
                        color: "primary.main",
                    },
                }}
            >
                {bookingTypeOptions?.map((option) => (
                    <Tab key={option.id} label={option.name} />
                ))}
            </Tabs>
            {(tabValue === 0 || tabValue === 1 || tabValue === 2) && <>
                {/* Title Section */}
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <ColorDropdown
                        colors={colors}
                        colorPicker={{ hex: color }}
                        handlePopper={handlePopper}
                        setColor={setColor}
                        setHoveredColor={setHoveredColor}
                        invertColor={invertColor}
                        colorsMap={colorsMap}
                    />
                    <OutlinedInput
                        disabled={isDisabled}
                        placeholder="Add title"
                        onChange={handleChange(setName)}
                        value={name}
                        sx={{
                            flex: 1,
                            maxWidth: '500px',
                            height: '35px',
                            '& .MuiOutlinedInput-notchedOutline': {
                                border: 'none',
                                borderBottom: '1px solid lightgray',
                            },
                        }}
                    />
                </Box>



                {/* Duration Section */}
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <AccessTime />
                    <Select
                        disabled={isDisabled}
                        value={bookingTypeLength}
                        onChange={(e) => setBookingTypeLength(e.target.value)}
                        variant="outlined"
                        sx={{
                            width: '20%',
                            minWidth: '100px',
                            height: '35px',
                            '& .MuiOutlinedInput-notchedOutline': {
                                border: 'none',
                            },
                        }}
                    >
                        {optionsSelect?.map((val) => (
                            <MenuItem value={val} key={val}>
                                {val}
                            </MenuItem>
                        ))}
                    </Select>

                    {bookingTypeLength === 'Custom' && (
                        <Box display="flex" gap={1}>
                            <OutlinedInput
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (/^\d*$/.test(value)) {
                                        setHrsOMins(value);
                                    }
                                }}
                                value={hrsOmins}
                                sx={{ height: '35px' }}
                                id="hrsOMins"
                            />
                            <Select
                                value={typeOTime}
                                onChange={(e) => setType(e.target.value)}
                                sx={{ height: '35px' }}
                            >
                                <MenuItem value="min">mins</MenuItem>
                                <MenuItem value="hrs">hrs</MenuItem>
                            </Select>
                        </Box>
                    )}
                </Box>

                {/* Location Section */}
                <Box display="flex" flexDirection="column" gap={1} mb={2}>
                    {/* Location Dropdown */}
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Place /> {/* Location icon */}
                        <Select
                            value={meetingLocationTypeKey}
                            variant="outlined"
                            displayEmpty
                            style={{
                                width: "100%", // Full width
                                maxWidth: "600px", // Increased max width
                                height: "60px", // Increased height for better click area
                                paddingLeft: "16px", // Adjusted padding for better click area
                                paddingRight: "16px", // Adjusted padding for better click area
                                border: "none",
                            }}
                            sx={{
                                bgcolor: 'background.default',
                                color: 'text.primary',
                                fontSize: "0.9rem", // Increased font size for better readability
                                textAlign: "left",
                                cursor: "pointer", // Show pointer cursor
                                ".MuiSelect-select": {
                                    display: "flex",
                                    alignItems: "center",
                                    padding: 0,
                                },
                                ".MuiOutlinedInput-notchedOutline": {
                                    display: "none", // Removes the border outline
                                },
                               
                                "&:hover": {
                                    backgroundColor: "action.hover",
                                },
                            }}
                            onChange={handleChange(setMeetingLocationTypeKey, setLocationType)}
                            renderValue={(selected) => {
                                if (!selected) {
                                    return (
                                        <span style={{ color: "#9e9e9e", cursor: "pointer" }}>
                                            Select Location
                                        </span>
                                    );
                                }

                                const selectedOption = MeetingOptions.find(option => option.key === selected);
                                return selectedOption ? selectedOption.label : selected;
                            }}
                        >
                            <MenuItem value="" disabled style={{ display: "none" }}>
                                Select Location
                            </MenuItem>

                            {MeetingOptions?.map((option) => (
                                <MenuItem
                                    key={option.key}
                                    value={option.key}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        height: "56px",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "row",
                                            alignItems: "center",
                                        }}
                                    >
                                        {option.icon}
                                        <h5 className="input-label">{option.label}</h5>
                                    </div>
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>

                    {/* Conditionally Render the MeetingLocation Component */}
                    {meetingLocationTypeKey && (
                        <Box sx={{
                            paddingLeft: "35px",
                            marginTop: meetingLocationTypeKey === MeetingTypes.PHONE_CALL.key ? "0px" : "-30px",
                        }}>
                            <MeetingLocation
                                customKey={meetingLocationTypeKey}  
                                locationInput={locationInput}
                                setLocationInput={setLocationInput}
                                phoneNumber={phoneNumber}
                                setPhoneNumber={setPhoneNumber}
                                canInviteeCall={canInviteeCall}
                                setCanInviteeCall={setCanInviteeCall}
                                isDisabled={isDisabled}
                                errors={errors}
                                redirectToVideo={redirectToVideo}
                                handleInputChange={handleInputChange}
                                handleRadioChange={handleRadioChange}
                            />
                        </Box>
                    )}
                </Box>



                {/* Description Section with rich text editor */}
                <Box display="flex" alignItems="center" gap={2} mt={5} ref={descRef}>
                    <IconButton onClick={handleDescriptionClick}>
                        <Notes />
                    </IconButton>
                    {editableDesc ? (
                        <Box
                            sx={{
                                flex: 1,
                                minWidth: '300px', // Set minimum width for a broader area
                                maxWidth: '500px',
                                maxHeight: '150px', // Fixed height to restrict size
                                overflowY: 'auto', // Enables vertical scrolling
                                padding: 1, // Optional: padding inside the box
                            }}
                        >
                            <ReactQuill
                                value={desc}
                                onChange={(value) => setDesc(value)} // Update the state with the rich text value
                                placeholder="Add description"
                                style={{ minHeight: '100px' }} // Minimum height for the editor
                                modules={{
                                    toolbar: [
                                        [{ 'header': '1' }, { 'header': '2' }, { 'font': [] }],
                                        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                                        ['bold', 'italic', 'underline'],
                                        ['link', 'image'],
                                        ['clean']                                         // Remove formatting button
                                    ],
                                }}
                                theme="snow" // Use 'snow' theme for a cleaner look
                            />
                        </Box>
                    ) : (
                        <Box
                            onClick={handleDescriptionClick} // Allow editing on click
                            sx={{
                                cursor: 'pointer',
                                minWidth: '300px', // Set minimum width for a broader area
                                maxWidth: '500px',
                                wordWrap: 'break-word',
                                color: desc ? 'text.primary' : 'text.secondary',
                                transition: 'color 0.2s',
                                '&:hover': {
                                    color: 'primary.main',
                                },
                                maxHeight: '150px', // Fixed height to restrict size when not editing
                                overflowY: 'auto', // Enables vertical scrolling for content display
                                padding: 1,
                            }}
                            dangerouslySetInnerHTML={{ __html: desc || 'Click to add description...' }} // Display content or prompt to add description
                        />
                    )}
                </Box>

            </>} 
        </Box>

    );
};
export default BookingBasicPart1;
